import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {SettingsService} from '../api/services/setting.service';
import {GeneralInfo} from '../api/interfaces/general-info';
import {MatInputModule} from '@angular/material/input';
import {MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {CommonModule} from '@angular/common';
import {UserService} from '../api/services/user.service';
import {MatSliderModule} from '@angular/material/slider';
import {AIProvider, User, UserAiSettings} from '../api/interfaces/user.interface';
import {DefaultValues, MODULE_NAMES, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {MatTooltipModule} from '@angular/material/tooltip';
import {SliderInputComponent} from '../shared/forms/slider-input/slider-input.component';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {MatChipsModule} from '@angular/material/chips';
import {MatListModule} from '@angular/material/list';
import {debounceTime, Subscription, switchMap} from 'rxjs';
import {HasPackageDirective} from '../packages/has-package.directive';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {getDoubleTapSetting, setDoubleTapSetting} from '../../cytoscape-extensions/double-tap.extension';
import {LinkTarget} from '../shared/pipes/link-target/link-target.enum';
import {LinkTargetSettings} from '../shared/pipes/link-target/link-target.settings';
import {LinkOptimizationMode} from '../shared/enums/link-optimization-mode.enum';
import {map} from 'lodash';
import {OpenaiService} from '../api/services/openai.service';
import {AiStateService} from '../services/ai-state.service';
import {DefaultStyleService} from '../api/services/default-style.service';
import {NotificationService} from '../services/notification.service';
import {PackageService} from '../api/services/package.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-settings-dialog',
  styles: [`
    .mat-column-actions {
      width: 32px;
      text-align: center;
      font-weight: bold;
    }

    ::ng-deep {

      maporium-settings-dialog {

        app-text-input {

          input {
            background-color: #ffffff00 !important;
          }

        }
      }


    }

    .invalid-key ::ng-deep input {
      border: 2px solid #cc0000 !important;
      background-color: #fae6e6 !important;
    }

    .valid-key ::ng-deep input {
      border: 2px solid #6aa84f !important;
      background-color: #edf5ea !important;
    }

  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0"><span class="icon-smap-user-settings me-2"></span>{{ 'TOOLBAR.SETTINGS' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="close()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <div mat-dialog-content>
      <form *ngIf="settingsForm" [formGroup]="settingsForm">
        <cdk-accordion [multi]="true" class="accordion">
          <maporium-accordion-item
            *appHasPackage="[PACKAGE_NAMES.developer]"
            [hidden]="!aiStateService.isAiVisible()"
            [title]="'SETTINGS_DIALOG.SECTION.AI_ASSISTANCE' | translate">

            <app-slide-toggle-input
              [label]="'SETTINGS_DIALOG.AI.USE_CUSTOM_API_KEYS' | translate"
              formControlName="useCustomApiKeys">
            </app-slide-toggle-input>

            <!-- Platform mode -->
            @if (!useCustomApiKeys) {
              <div class="d-flex align-items-center gap-2 py-2"
                   style="color: #888; font-size: 0.85rem;">
                <span class="material-symbols-rounded">auto_awesome</span>
                {{ 'SETTINGS_DIALOG.AI.POWERED_BY' | translate }}
              </div>
            }

            <!-- Custom keys mode -->
            @if (useCustomApiKeys) {
            <div>
              <app-select-input
                formControlName="aiProvider"
                [options]="providerOptions"
                [isMaporiumIcon]="true"
                [label]="'SETTINGS_DIALOG.AI.PROVIDER' | translate"
                [placeholder]="'SETTINGS_DIALOG.AI.SELECT_PROVIDER' | translate">
              </app-select-input>

              <!-- OpenAI Key Input -->
              <app-text-input
                *ngIf="selectedProvider === 'openai'"
                formControlName="openaiApiKey"
                type="password"
                class="masked-input"
                [pasteOnly]="true"
                [class.invalid-key]="isOpenAIKeyInvalid"
                [class.valid-key]="isOpenAIKeyValid"
                [label]="'SETTINGS_DIALOG.AI.OPENAI_KEY' | translate"
                [placeholder]="'SETTINGS_DIALOG.AI.OPENAI_KEY_PLACEHOLDER' | translate">
              </app-text-input>

              <!-- Anthropic Key Input -->
              <app-text-input
                *ngIf="selectedProvider === 'anthropic'"
                formControlName="anthropicApiKey"
                type="password"
                class="masked-input"
                [pasteOnly]="true"
                [class.invalid-key]="isAnthropicKeyInvalid"
                [class.valid-key]="isAnthropicKeyValid"
                [label]="'SETTINGS_DIALOG.AI.ANTHROPIC_KEY' | translate"
                [placeholder]="'SETTINGS_DIALOG.AI.ANTHROPIC_KEY_PLACEHOLDER' | translate">
              </app-text-input>

              <!-- Gemini Key Input -->
              <app-text-input
                *ngIf="selectedProvider === 'gemini'"
                formControlName="geminiApiKey"
                type="password"
                class="masked-input"
                [pasteOnly]="true"
                [class.invalid-key]="isGeminiKeyInvalid"
                [class.valid-key]="isGeminiKeyValid"
                [label]="'SETTINGS_DIALOG.AI.GEMINI_KEY' | translate"
                [placeholder]="'SETTINGS_DIALOG.AI.GEMINI_KEY_PLACEHOLDER' | translate">
              </app-text-input>

              <app-select-input
                *ngIf="isCurrentProviderKeyValid"
                formControlName="modelVersion"
                [options]="availableModels"
                [isMaporiumIcon]="true"
                [label]="'SETTINGS_DIALOG.AI.MODEL_VERSION' | translate"
                [placeholder]="'SETTINGS_DIALOG.AI.SELECT_MODEL' | translate">
              </app-select-input>
            </div>
            }
          </maporium-accordion-item>

          <maporium-accordion-item
            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]"
            [title]="'SETTINGS_DIALOG.SECTION.ALIGNMENT' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.ALIGNMENT.DISPLAY_GUIDES' | translate" formControlName="showAlignLines"></app-slide-toggle-input>
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.ALIGNMENT.DISPLAY_GRID_LINES' | translate" formControlName="showGrid"></app-slide-toggle-input>
            <app-slider-input formControlName="gridIntensity"
                              [min]="0.1"
                              [label]="'SETTINGS_DIALOG.ALIGNMENT.GRID_INTENSITY' | translate"
                              [max]="0.5"
                              [step]="0.1"></app-slider-input>
            <app-slider-input formControlName="gridSize"
                              [min]="5"
                              [label]="'SETTINGS_DIALOG.ALIGNMENT.GRID_SIZE' | translate"
                              [max]="25"
                              [step]="1"></app-slider-input>
            <app-select-input formControlName="snapOptions"
                              [placeholder]="'SETTINGS_DIALOG.ALIGNMENT.SNAPPING' | translate"
                              [hasBlank]="true"
                              [blankValue]="'none'"
                              [label]="'SETTINGS_DIALOG.ALIGNMENT.SNAPPING' | translate"
                              [options]="snapOptions">
            </app-select-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.APPEARANCE' | translate">
            <app-select-input
              formControlName="defaultAppearance"
              [options]="appearanceOptions"
              [label]="'SETTINGS_DIALOG.APPEARANCE.DEFAULT_APPEARANCE' | translate"
              [placeholder]="'SETTINGS_DIALOG.APPEARANCE.SYSTEM' | translate">
            </app-select-input>
            <app-slider-input formControlName="nodeOverlayOpacity"
                              [min]="0"
                              [label]="'SETTINGS_DIALOG.APPEARANCE.SELECTION_OPACITY' | translate"
                              [max]="1"
                              [step]="0.1"></app-slider-input>
            <app-slider-input formControlName="nodeDragOpacity"
                              [min]="0"
                              [label]="'SETTINGS_DIALOG.APPEARANCE.DRAGGING_OPACITY' | translate"
                              [max]="1"
                              [step]="0.1"></app-slider-input>
            <app-slider-input formControlName="nodeDropOverlayOpacity"
                              [min]="0"
                              [label]="'SETTINGS_DIALOG.APPEARANCE.DROPPING_OPACITY' | translate"
                              [max]="1"
                              [step]="0.1"></app-slider-input>
            <app-slider-input formControlName="filteringOpacity"
                              [min]="0.1"
                              [label]="'SETTINGS_DIALOG.APPEARANCE.FILTERING_OPACITY' | translate"
                              [max]="0.5"
                              [step]="0.1"></app-slider-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.PERFORMANCE' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_SMAP_SCALING' | translate"
                                   formControlName="textureRenderer"
                                   [matTooltip]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_SMAP_SCALING_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_LINK_LABELING' | translate"
                                   formControlName="linkOptimizationToggle"
                                   [matTooltip]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_LINK_LABELING_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_LINK_SELECTION' | translate"
                                   formControlName="optimizeLinkSelection"
                                   [matTooltip]="'SETTINGS_DIALOG.PERFORMANCE.OPTIMIZE_LINK_SELECTION_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.EDITING' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.EDITING.APPLY_TO_MULTISELECTION' | translate"
                                   formControlName="applyEditsToMultiselection"
                                   [matTooltip]="'SETTINGS_DIALOG.EDITING.APPLY_TO_MULTISELECTION_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            *ngIf="canManagePublisherName"
            [title]="'SETTINGS_DIALOG.SECTION.PUBLISHING' | translate">
            <app-select-input [formControl]="publisherNameSetting"
                              [label]="'SETTINGS_DIALOG.PUBLISHING.PUBLISH_SMAPS_UNDER' | translate"
                              [options]="publisherNameOptions"
                              [isMaporiumIcon]="true"></app-select-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.INFORMATION' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.INFORMATION.OPEN_PANEL_ON_FOCUS' | translate"
                                   formControlName="openInfoPanelOnFocus"
                                   [matTooltip]="'SETTINGS_DIALOG.OTHER.OPEN_INFO_PANEL_ON_FOCUS_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.OTHER' | translate">
            <app-slider-input formControlName="soundVolume"
                              [min]="0"
                              [label]="'SETTINGS_DIALOG.OTHER.SOUND_VOLUME' | translate"
                              [max]="1"
                              [step]="0.1"></app-slider-input>
            <app-slider-input [formControl]="doubleTapDelay"
                              [label]="'SETTINGS_DIALOG.OTHER.DOUBLE_TAP_DELAY' | translate"
                              [min]="100"
                              [max]="1000"
                              [step]="50"></app-slider-input>
            <app-select-input formControlName="linkTarget"
                              [options]="linkTargetOptions"
                              [label]="'SETTINGS_DIALOG.OTHER.RESOURCE_DISPLAY' | translate"></app-select-input>
            <!--        <app-slider-input formControlName="elementsDimming"-->
            <!--                           [min]="0"-->
            <!--                          [label]="'Filter Dimming'"-->
            <!--                          [max]="0.5"-->
            <!--                          [step]="0.05"></app-slider-input>-->

            <div class="row mt-3 align-items-center no-gutter">
              <div class="col-4 pe-1">
                <label class="form-label m-0">{{ 'SETTINGS_DIALOG.OTHER.DEFAULT_STYLES' | translate }}</label>
              </div>
              <div class="col-8">
                <button type="button"
                        class="dialog-action-btn dialog-action-btn-cancel"
                        style="width: 100%; white-space: nowrap; font-size: 13px; margin: 0;"
                        (click)="resetDefaultStyles()">
                  {{ 'SETTINGS_DIALOG.OTHER.RESET_TO_APPLICATION_DEFAULTS' | translate }}
                </button>
              </div>
            </div>

            <div class="row mt-3 align-items-center no-gutter">
              <div class="col-4 pe-1">
                <label class="form-label m-0">{{ 'SETTINGS_DIALOG.OTHER.USER_SETTINGS' | translate }}</label>
              </div>
              <div class="col-8">
                <button type="button"
                        class="dialog-action-btn dialog-action-btn-cancel"
                        style="width: 100%; white-space: nowrap; font-size: 13px; margin: 0;"
                        (click)="resetUserSettings()">
                  {{ 'SETTINGS_DIALOG.OTHER.RESET_TO_FACTORY_SETTINGS' | translate }}
                </button>
              </div>
            </div>

          </maporium-accordion-item>

          <maporium-accordion-item
            [title]="'SETTINGS_DIALOG.SECTION.TOOLTIPS' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.TOOLTIPS.TOOLTIP_DISPLAY' | translate"
                                   formControlName="tooltipsEnabled"
                                   [matTooltip]="'SETTINGS_DIALOG.TOOLTIPS.TOOLTIP_DISPLAY_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.TOOLTIPS.TOOLTIP_FADING' | translate"
                                   formControlName="tooltipFadingEnabled"
                                   [matTooltip]="'SETTINGS_DIALOG.TOOLTIPS.TOOLTIP_FADING_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
          </maporium-accordion-item>

          <maporium-accordion-item
            *appHasPackage="[packageNames.developer]"
            [title]="'SETTINGS_DIALOG.SECTION.EXPERIMENTAL' | translate">
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.EXPERIMENTAL.WEBGL_RENDERING' | translate"
                                   formControlName="webglEnabled"></app-slide-toggle-input>
            <app-slide-toggle-input [label]="'SETTINGS_DIALOG.EXPERIMENTAL.LIVE_SMAP_PREVIEWS' | translate"
                                   formControlName="livePreviewsEnabled"
                                   [matTooltip]="'SETTINGS_DIALOG.EXPERIMENTAL.LIVE_SMAP_PREVIEWS_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
            <app-slide-toggle-input *ngIf="settingsForm?.get('livePreviewsEnabled')?.value"
                                   [label]="'SETTINGS_DIALOG.EXPERIMENTAL.SNAPSHOT_MODE' | translate"
                                   formControlName="snapshotPreviewsEnabled"
                                   [matTooltip]="'SETTINGS_DIALOG.EXPERIMENTAL.SNAPSHOT_MODE_TOOLTIP' | translate"
                                   [matTooltipPosition]="'right'"></app-slide-toggle-input>
          </maporium-accordion-item>
        </cdk-accordion>
      </form>
    </div>

  `,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatSliderModule,
    MatTooltipModule,
    SliderInputComponent,
    TextInputComponent,
    SlideToggleInputComponent,
    MatChipsModule,
    MatListModule,
    HasPackageDirective,
    SelectInputComponent,
    MaporiumAccordionItemComponent,
    CdkAccordion,
    TranslatePipe
  ],
  standalone: true
})
export class SettingsDialogComponent implements OnInit, OnDestroy {
  public readonly MODULE_NAMES = MODULE_NAMES;

  refreshNeeded = false;
  currentUser: User | undefined;
  packageNames = PACKAGE_NAMES;

  // AI Assistance properties
  useCustomApiKeys = false;
  private _ignoreCustomKeysChanges = false;
  selectedProvider: AIProvider = 'openai';
  isOpenAIKeyValid = false;
  isOpenAIKeyInvalid = false;
  isAnthropicKeyValid = false;
  isAnthropicKeyInvalid = false;
  isGeminiKeyValid = false;
  isGeminiKeyInvalid = false;

  providerOptions = [
    {key: 'openai', value: 'OpenAI GPT', icon: 'icon-smap-provider-openai-gpt'},
    {key: 'anthropic', value: 'Anthropic Claude', icon: 'icon-smap-provider-anthropic-claude'},
    {key: 'gemini', value: 'Google Gemini', icon: 'icon-smap-provider-google-gemini'}
  ];

  openaiModels = [
    {key: 'gpt-5.1', value: 'GPT-5.1', icon: 'icon-smap-provider-openai-gpt'},
  ];

  anthropicModels = [
    {key: 'claude-sonnet-5', value: 'Claude 5 Sonnet', icon: 'icon-smap-provider-anthropic-claude'},
    {key: 'claude-sonnet-4-6', value: 'Claude 4.6 Sonnet', icon: 'icon-smap-provider-anthropic-claude'},
    {key: 'claude-opus-4-8', value: 'Claude 4.8 Opus', icon: 'icon-smap-provider-anthropic-claude'},
    {key: 'claude-haiku-4-5-20251001', value: 'Claude 4.5 Haiku', icon: 'icon-smap-provider-anthropic-claude'},
    {key: 'claude-sonnet-4-5-20250929', value: 'Claude 4.5 Sonnet', icon: 'icon-smap-provider-anthropic-claude'}
  ];

  geminiModels = [
    {key: 'gemini-2.5-pro', value: 'Gemini 2.5 Pro', icon: 'icon-smap-provider-google-gemini'},
    {key: 'gemini-2.5-flash', value: 'Gemini 2.5 Flash', icon: 'icon-smap-provider-google-gemini'},
    {key: 'gemini-2.5-flash-lite', value: 'Gemini 2.5 Flash Lite', icon: 'icon-smap-provider-google-gemini'},
    {key: 'gemini-2.0-flash', value: 'Gemini 2.0 Flash', icon: 'icon-smap-provider-google-gemini'}
  ];

  availableModels = this.openaiModels;

  get isCurrentProviderKeyValid(): boolean {
    switch (this.selectedProvider) {
      case 'openai':
        return this.isOpenAIKeyValid;
      case 'anthropic':
        return this.isAnthropicKeyValid;
      case 'gemini':
        return this.isGeminiKeyValid;
      default:
        return false;
    }
  }

  snapOptions = [
    { key: 'GRID', value: 'grid', icon: 'grid_on' },
    { key: 'GUIDES', value: 'guides', icon: 'grid_guides' }
  ];

  appearanceOptions = [
    { key: 'system', value: 'system', icon: 'computer', isSeparatorAfter: true },
    { key: 'light', value: 'light', icon: 'light_mode' },
    { key: 'dark', value: 'dark', icon: 'dark_mode' }
  ];
  linkTargetOptions = map(LinkTargetSettings, (value, key) => {
    return {
      key: value.target.toString(),
      value: value.label,
      icon: value.icon
    };
  });
  settingsForm: FormGroup | undefined;
  doubleTapDelay = new FormControl(getDoubleTapSetting());
  publisherNameSetting = new FormControl<'owner' | 'organization'>('organization');
  publisherNameOptions: { key: string; value: string; icon: string }[] = [];
  private subs = new Subscription();

  get canManagePublisherName(): boolean {
    return this.packageService.canManagePublisherName();
  }

  constructor(
    private settingsService: SettingsService,
    private matDialogRef: MatDialogRef<SettingsDialogComponent>,
    private userService: UserService,
    private dialog: MatDialog,
    private openaiService: OpenaiService,
    protected aiStateService: AiStateService,
    private defaultStyleService: DefaultStyleService,
    private notificationService: NotificationService,
    private packageService: PackageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { focusApiKey?: boolean } = {}
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userService.getCurrentUserFromStorage();
    this.initPublisherNameSetting();
    const settings = this.settingsService.getLocalStorageSettings();
    const getGeneralInfoSub = this.settingsService.getGeneralInfo()
      .subscribe((generalInfo) => {
        this.settingsForm = new FormGroup({
          nodeOverlayOpacity: new FormControl(generalInfo?.nodeOverlayOpacity || SettingsService.sharedValues.node.nodeOverlaySelectOpacity),
          nodeDropOverlayOpacity: new FormControl(generalInfo?.nodeDropOverlayOpacity || SettingsService.sharedValues.node.nodeDropOverlayOpacity),
          nodeDragOpacity: new FormControl(generalInfo?.nodeDragOpacity || SettingsService.sharedValues.node.nodeDragOpacity),
          nodeHighlightColor: new FormControl(generalInfo?.nodeHighlightColor || SettingsService.sharedValues.node.nodeHighlightColor),
          useCustomApiKeys: new FormControl(false),
          aiProvider: new FormControl('openai'),
          openaiApiKey: new FormControl(''),
          anthropicApiKey: new FormControl(''),
          geminiApiKey: new FormControl(''),
          modelVersion: new FormControl(''),
          elementsDimming: new FormControl(generalInfo.elementsDimming),
          filteringOpacity: new FormControl(generalInfo?.filteringOpacity ?? DefaultValues.filteringOpacity),
          showGrid: new FormControl(generalInfo.showGrid || false),
          showAlignLines: new FormControl(generalInfo.showAlignLines || false),
          gridIntensity: new FormControl(settings.gridIntensity || 0.3),
          gridSize: new FormControl(generalInfo.gridSize),
          snapToAlignLines: new FormControl(generalInfo.snapToAlignLines),
          snapToGrid: new FormControl(generalInfo.snapToGrid),
          linkTarget: new FormControl(settings.linkTarget !== undefined ? settings.linkTarget.toString() : LinkTarget.SingleWindow.toString()),
          snapOptions: new FormControl(generalInfo.snapToGrid ? 'GRID' : generalInfo.snapToAlignLines ? 'GUIDES' : undefined),
          soundVolume: new FormControl(generalInfo.soundVolume ?? 1),
          defaultAppearance: new FormControl(settings.defaultAppearance || DefaultValues.appearance.default),
          webglEnabled: new FormControl(settings.webgl ?? false),
          textureRenderer: new FormControl(settings.textureRenderer ?? false),
          linkOptimizationMode: new FormControl(settings.linkOptimizationMode || LinkOptimizationMode.NONE),
          // Toggle for link optimization: false (default) = NONE (all labels visible), true = RELATED (hide attached labels)
          linkOptimizationToggle: new FormControl(settings.linkOptimizationMode === LinkOptimizationMode.RELATED),
          livePreviewsEnabled: new FormControl(settings.livePreviewsEnabled ?? true),
          snapshotPreviewsEnabled: new FormControl(settings.snapshotPreviewsEnabled ?? true),
          tooltipsEnabled: new FormControl(settings.tooltipsEnabled ?? true),
          tooltipFadingEnabled: new FormControl(settings.tooltipFadingEnabled ?? false),
          optimizeLinkSelection: new FormControl(settings.optimizeLinkSelection ?? false),
          applyEditsToMultiselection: new FormControl(settings.applyEditsToMultiselection ?? false),
          openInfoPanelOnFocus: new FormControl(settings.openInfoPanelOnFocus ?? true),
        });
        this.subs.add(this.settingsForm.valueChanges
          .pipe(debounceTime(500))
          .subscribe(() => {
            if (this.settingsForm === undefined) return;
            if (this.settingsForm.invalid) return;
            const raw = this.settingsForm.getRawValue();

            if (raw.snapOptions !== undefined) {
              switch (raw.snapOptions) {
                case 'GRID':
                  raw.snapToGrid = true;
                  raw.snapToAlignLines = false;
                  break;
                case 'GUIDES':
                  raw.snapToGrid = false;
                  raw.snapToAlignLines = true;
                  break;
              }
            } else {
              raw.snapToGrid = false;
              raw.snapToAlignLines = false;
            }

            // Convert toggle to enum: false (default) = NONE (show all labels), true = RELATED (hide attached labels)
            const linkOptimizationMode = raw.linkOptimizationToggle ? LinkOptimizationMode.RELATED : LinkOptimizationMode.NONE;

            // Check if experimental or performance settings that require refresh have changed
            if (settings.webgl !== raw.webglEnabled ||
                settings.textureRenderer !== raw.textureRenderer ||
                settings.linkOptimizationMode !== linkOptimizationMode) {
              this.refreshNeeded = true;
            }

            settings.linkTarget = raw.linkTarget;
            settings.gridIntensity = raw.gridIntensity;
            settings.defaultAppearance = raw.defaultAppearance;
            settings.webgl = raw.webglEnabled;
            settings.textureRenderer = raw.textureRenderer;
            settings.linkOptimizationMode = linkOptimizationMode;
            settings.livePreviewsEnabled = raw.livePreviewsEnabled;
            settings.snapshotPreviewsEnabled = raw.snapshotPreviewsEnabled;
            settings.tooltipsEnabled = raw.tooltipsEnabled;
            settings.tooltipFadingEnabled = raw.tooltipFadingEnabled;
            settings.optimizeLinkSelection = raw.optimizeLinkSelection;
            settings.applyEditsToMultiselection = raw.applyEditsToMultiselection;
            settings.openInfoPanelOnFocus = raw.openInfoPanelOnFocus;
            this.settingsService.setLocalStorageSettings(settings);

            this.subs.add(this.settingsService.saveGeneralInfo(raw as unknown as GeneralInfo)
              .subscribe((res) => {
                if (res.nodeOverlayOpacity !== undefined) {
                  SettingsService.sharedValues.node.nodeOverlaySelectOpacity = res.nodeOverlayOpacity;
                }
                if (res.nodeDragOpacity !== undefined) {
                  SettingsService.sharedValues.node.nodeDragOpacity = res.nodeDragOpacity;
                }

                if (res.nodeDropOverlayOpacity !== undefined) {
                  SettingsService.sharedValues.node.nodeDropOverlayOpacity = res.nodeDropOverlayOpacity;
                }

                this.settingsService.generalInfoChanged.next(raw as unknown as GeneralInfo);
              }));
          }));

        this.subs.add(
          this.userService.getAiSettings().subscribe((aiSettings) => {
            if (this.settingsForm === undefined) return;

            // Load useCustomApiKeys toggle
            this.useCustomApiKeys = !!aiSettings.useCustomApiKeys;
            this._ignoreCustomKeysChanges = true;
            this.settingsForm.get('useCustomApiKeys')?.setValue(this.useCustomApiKeys, {emitEvent: false});
            // Allow the slide-toggle component to finish initializing before accepting changes
            setTimeout(() => this._ignoreCustomKeysChanges = false, 500);

            // Load provider and keys from AI settings
            this.selectedProvider = aiSettings.provider || 'openai';
            this.settingsForm.get('aiProvider')?.patchValue(this.selectedProvider);
            this.settingsForm.get('openaiApiKey')?.patchValue(aiSettings.openaiKey || '');
            this.settingsForm.get('anthropicApiKey')?.patchValue(aiSettings.anthropicKey || '');
            this.settingsForm.get('geminiApiKey')?.patchValue(aiSettings.geminiKey || '');

            // Load model based on current provider
            const currentModel = this.getModelForProvider(aiSettings, this.selectedProvider);
            this.settingsForm.get('modelVersion')?.patchValue(currentModel || this.getDefaultModel(this.selectedProvider));

            // Update available models based on provider
            this.updateAvailableModels(this.selectedProvider);

            // Validate existing keys
            if (aiSettings.openaiKey) this.validateApiKey('openai', aiSettings.openaiKey);
            if (aiSettings.anthropicKey) this.validateApiKey('anthropic', aiSettings.anthropicKey);
            if (aiSettings.geminiKey) this.validateApiKey('gemini', aiSettings.geminiKey);

            // useCustomApiKeys toggle handler
            this.subs.add(
              this.settingsForm.get('useCustomApiKeys')?.valueChanges
                .subscribe((value: boolean) => {
                  if (this._ignoreCustomKeysChanges) {
                    this.settingsForm?.get('useCustomApiKeys')?.setValue(this.useCustomApiKeys, {emitEvent: false});
                    return;
                  }
                  this.useCustomApiKeys = value;
                })
            );
            this.subs.add(
              this.settingsForm.get('useCustomApiKeys')?.valueChanges
                .pipe(debounceTime(300))
                .subscribe((useCustomApiKeys: boolean) => {
                  if (this._ignoreCustomKeysChanges) return;
                  this.userService.updateAiSettings({useCustomApiKeys}).subscribe(() => {
                    this.aiStateService.refreshAiSettings();
                  });
                })
            );

            // Provider change handler
            this.subs.add(
              this.settingsForm.get('aiProvider')?.valueChanges
                .pipe(debounceTime(300))
                .subscribe((provider: AIProvider) => {
                  if (this._ignoreCustomKeysChanges) return;
                  this.selectedProvider = provider;
                  this.updateAvailableModels(provider);
                  const modelForProvider = this.getModelForProvider(aiSettings, provider);
                  this.settingsForm?.get('modelVersion')?.setValue(modelForProvider || this.getDefaultModel(provider));
                  this.userService.updateAiSettings({provider}).subscribe(() => this.refreshNeeded = true);
                })
            );

            // OpenAI API key handler
            this.subs.add(
              this.settingsForm.get('openaiApiKey')?.valueChanges
                .pipe(debounceTime(300))
                .subscribe((apiKey: string) => {
                  if (this._ignoreCustomKeysChanges) return;
                  this.validateApiKey('openai', apiKey);
                  this.userService.updateAiSettings({openaiKey: apiKey || ''}).subscribe(() => this.refreshNeeded = true);
                })
            );

            // Anthropic API key handler
            this.subs.add(
              this.settingsForm.get('anthropicApiKey')?.valueChanges
                .pipe(debounceTime(300))
                .subscribe((apiKey: string) => {
                  if (this._ignoreCustomKeysChanges) return;
                  this.validateApiKey('anthropic', apiKey);
                  this.userService.updateAiSettings({anthropicKey: apiKey || ''}).subscribe(() => this.refreshNeeded = true);
                })
            );

            // Gemini API key handler
            this.subs.add(
              this.settingsForm.get('geminiApiKey')?.valueChanges
                .pipe(debounceTime(300))
                .subscribe((apiKey: string) => {
                  if (this._ignoreCustomKeysChanges) return;
                  this.validateApiKey('gemini', apiKey);
                  this.userService.updateAiSettings({geminiKey: apiKey || ''}).subscribe(() => this.refreshNeeded = true);
                })
            );

            // Model version change handler - saves to the current provider's model field
            this.subs.add(
              this.settingsForm.get('modelVersion')?.valueChanges
                .pipe(debounceTime(500))
                .subscribe((model: string) => {
                  if (model) {
                    const modelUpdate = this.getModelUpdateForProvider(this.selectedProvider, model);
                    this.userService.updateAiSettings(modelUpdate).subscribe(() => this.refreshNeeded = true);
                  }
                })
            );

          })
        );
      });
    const doubleTapDelaySub = this.doubleTapDelay.valueChanges.subscribe(
      (value) => {
        setDoubleTapSetting(value || DefaultValues.doubleTapDelay);
      }
    );
    this.subs.add(getGeneralInfoSub);
    this.subs.add(doubleTapDelaySub);

    // Focus on API key input if requested from AI bubble
    if (this.data?.focusApiKey) {
      const dialogElement = document.querySelector('maporium-settings-dialog');
      if (dialogElement) {
        const observer = new MutationObserver((mutations) => {
          const apiKeyInput = dialogElement.querySelector('app-text-input[formcontrolname="openaiApiKey"] input') as HTMLInputElement;
          if (apiKeyInput) {
            // Ensure focus after a longer delay to allow any competing focus events to settle
            setTimeout(() => {
              apiKeyInput.focus();
              observer.disconnect();
            }, 300);
          }
        });

        observer.observe(dialogElement, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initPublisherNameSetting(): void {
    if (!this.canManagePublisherName) return;
    this.buildPublisherNameOptions();
    this.publisherNameSetting.setValue(this.currentUser?.publisherNameSetting || 'organization', {emitEvent: false});

    this.subs.add(this.publisherNameSetting.valueChanges.subscribe((value) => {
      if (!value || !this.currentUser) return;
      const updatedUser = {...this.currentUser, publisherNameSetting: value} as User;
      this.userService.update(updatedUser)
        .pipe(switchMap(() => this.userService.refreshLocalUser()))
        .subscribe({
          next: (user) => this.currentUser = user,
          error: () => this.notificationService.error(this.translate.instant('SETTINGS_DIALOG.PUBLISHING.SAVE_FAILED'))
        });
    }));
  }

  private buildPublisherNameOptions(): void {
    this.publisherNameOptions = [
      {key: 'owner', value: this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_OWNER'), icon: 'icon-smap-account-individual'},
      {key: 'organization', value: this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_ORGANIZATION'), icon: 'icon-smap-account-enterprise'}
    ];
  }

  refresh() {
    location.reload();
  }

  close() {
    if (this.refreshNeeded) {
      this.refresh();
    } else {
      this.matDialogRef.close();
    }
  }

  openConfirmationDialog() {
    if (!this.refreshNeeded) {
      const ref = this.dialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        data: {
          title: this.translate.instant('SETTINGS_DIALOG.REFRESH.TITLE'),
          message: this.translate.instant('SETTINGS_DIALOG.REFRESH.MESSAGE'),
          cancelText: this.translate.instant('SETTINGS_DIALOG.REFRESH.CLOSE'),
          confirmText: this.translate.instant('SETTINGS_DIALOG.REFRESH.REFRESH_NOW')
        }
      });
      ref.afterClosed().subscribe((result) => {
        if (result) {
          this.refresh();
        }
      });
    }
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;

  private updateAvailableModels(provider: AIProvider): void {
    switch (provider) {
      case 'openai':
        this.availableModels = this.openaiModels;
        break;
      case 'anthropic':
        this.availableModels = this.anthropicModels;
        break;
      case 'gemini':
        this.availableModels = this.geminiModels;
        break;
    }
  }

  private getDefaultModel(provider: AIProvider): string {
    switch (provider) {
      case 'openai':
        return 'gpt-5.1';
      case 'anthropic':
        return 'claude-sonnet-5';
      case 'gemini':
        return 'gemini-2.5-flash';
      default:
        return 'gpt-5.1';
    }
  }

  private getModelForProvider(settings: UserAiSettings, provider: AIProvider): string | undefined {
    switch (provider) {
      case 'openai':
        return settings.openaiModel;
      case 'anthropic':
        return settings.anthropicModel;
      case 'gemini':
        return settings.geminiModel;
      default:
        return settings.openaiModel;
    }
  }

  private getModelUpdateForProvider(provider: AIProvider, model: string): Partial<UserAiSettings> {
    switch (provider) {
      case 'openai':
        return {openaiModel: model};
      case 'anthropic':
        return {anthropicModel: model};
      case 'gemini':
        return {geminiModel: model};
      default:
        return {openaiModel: model};
    }
  }

  private validateApiKey(provider: AIProvider, apiKey: string): void {
    const trimmedKey = apiKey?.trim() || '';

    if (!trimmedKey) {
      this.setKeyValidation(provider, false, false);
      return;
    }

    const patterns: Record<AIProvider, RegExp> = {
      openai: /^(?:sk|sk-proj|sk-live|sk-test)-[A-Za-z0-9_-]{20,}$/,
      anthropic: /^sk-ant-[A-Za-z0-9_-]{20,}$/,
      gemini: /^AIza[A-Za-z0-9_-]{30,}$/
    };

    const valid = patterns[provider].test(trimmedKey);
    this.setKeyValidation(provider, valid, !valid);

    if (provider === 'openai') {
      this.aiStateService.setOpenAIKeyValid(valid);
    }
  }

  private setKeyValidation(provider: AIProvider, valid: boolean, invalid: boolean): void {
    switch (provider) {
      case 'openai':
        this.isOpenAIKeyValid = valid;
        this.isOpenAIKeyInvalid = invalid;
        break;
      case 'anthropic':
        this.isAnthropicKeyValid = valid;
        this.isAnthropicKeyInvalid = invalid;
        break;
      case 'gemini':
        this.isGeminiKeyValid = valid;
        this.isGeminiKeyInvalid = invalid;
        break;
    }
  }

  resetDefaultStyles(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('SETTINGS_DIALOG.RESET_STYLES.TITLE'),
        message: this.translate.instant('SETTINGS_DIALOG.RESET_STYLES.MESSAGE'),
        confirmText: this.translate.instant('SETTINGS_DIALOG.RESET_STYLES.CONFIRM'),
        cancelText: this.translate.instant('CREATE_EDIT_SMAP.ACTIONS.CANCEL')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.defaultStyleService.resetDefaultStyles().subscribe({
          next: () => {
            this.notificationService.success(this.translate.instant('SETTINGS_DIALOG.RESET_STYLES.SUCCESS'));
          },
          error: (error) => {
            console.error('Error resetting default styles:', error);
            this.notificationService.error(this.translate.instant('SETTINGS_DIALOG.RESET_STYLES.FAILED'));
          }
        });
      }
    });
  }

  resetUserSettings(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('SETTINGS_DIALOG.RESET_USER_SETTINGS.TITLE'),
        message: this.translate.instant('SETTINGS_DIALOG.RESET_USER_SETTINGS.MESSAGE'),
        confirmText: this.translate.instant('SETTINGS_DIALOG.RESET_USER_SETTINGS.CONFIRM'),
        cancelText: this.translate.instant('CREATE_EDIT_SMAP.ACTIONS.CANCEL')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.settingsService.resetToFactorySettings().subscribe({
          next: () => {
            this.matDialogRef.close();
            this.notificationService.success(this.translate.instant('SETTINGS_DIALOG.RESET_USER_SETTINGS.SUCCESS'));
            // Give the notification a moment to render before the refresh unloads the page.
            setTimeout(() => this.refresh(), 1000);
          },
          error: (error) => {
            console.error('Error resetting user settings:', error);
            this.notificationService.error(this.translate.instant('SETTINGS_DIALOG.RESET_USER_SETTINGS.FAILED'));
          }
        });
      }
    });
  }
}
